import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { addMonths, format, subMonths } from 'date-fns'
import { pl } from 'date-fns/locale'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  MoveHorizontal,
} from 'lucide-react'
import { toast } from 'sonner'

import type { VacationType } from '@czas/shared'

import Alert from '@/components/ui/Alert'
import Button from '@/components/ui/Button'
import { apiErrorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import {
  downloadMonthlyReportPdf,
  downloadMonthlyXlsx,
  useMonthlyReport,
} from '@/features/admin/use-reports'

/**
 * Kolumny dni nieobecności w tabeli zespołu. Skróty, bo pełne nazwy typów
 * („opieka nad dzieckiem") rozjeżdżają tabelę — pełna nazwa siedzi w `title`.
 */
const VACATION_COLUMNS: readonly {
  type: VacationType
  short: string
  title: string
}[] = [
  { type: 'WYPOCZYNKOWY', short: 'Wyp.', title: 'Urlop wypoczynkowy (100% dnia)' },
  { type: 'NA_ZADANIE', short: 'Żąd.', title: 'Urlop na żądanie (100% dnia)' },
  {
    type: 'OPIEKA_NAD_DZIECKIEM',
    short: 'Op.',
    title: 'Opieka nad dzieckiem, art. 188 KP (100% dnia)',
  },
  {
    type: 'CHOROBOWY',
    short: 'L4',
    title: 'Zwolnienie lekarskie — zalicza 80% dnia pracy',
  },
  {
    type: 'BEZPLATNY',
    short: 'Bezpł.',
    title: 'Urlop bezpłatny — nie wlicza się do godzin ani do wynagrodzenia',
  },
] as const

const VACATION_SHORT: Record<VacationType, string> = {
  WYPOCZYNKOWY: 'Wypoczynkowy',
  NA_ZADANIE: 'Na żądanie',
  OPIEKA_NAD_DZIECKIEM: 'Opieka',
  CHOROBOWY: 'L4',
  BEZPLATNY: 'Bezpłatny',
}

/**
 * Przypięta pierwsza kolumna tabeli. Admin i manager czytają raport z telefonu,
 * gdzie 14 kolumn nie mieści się na ekranie — bez tego przy przewinięciu w bok
 * nie wiadomo, czyj wiersz się ogląda. Cień na prawej krawędzi sygnalizuje,
 * że treść chowa się pod spodem.
 */
const STICKY_COL =
  'sticky left-0 shadow-[1px_0_0_0_rgb(var(--border-subtle))]'

/** 1 osoba / 2 osoby / 5 osób. */
function pluralOsoby(n: number): string {
  if (n === 1) return 'osoba'
  const last = n % 10
  const lastTwo = n % 100
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) return 'osoby'
  return 'osób'
}

function parseYearMonth(value: string | null): string {
  if (value && /^\d{4}-(0[1-9]|1[0-2])$/.test(value)) return value
  return format(new Date(), 'yyyy-MM')
}

function yearMonthToDate(yearMonth: string): Date {
  const [y, m] = yearMonth.split('-').map(Number)
  return new Date(y!, m! - 1, 1)
}

function formatPolishMonth(yearMonth: string): string {
  return format(yearMonthToDate(yearMonth), 'LLLL yyyy', { locale: pl })
}

export default function ReportTeam() {
  const [params, setParams] = useSearchParams()
  const yearMonth = parseYearMonth(params.get('ym'))
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [downloadingXlsx, setDownloadingXlsx] = useState(false)

  const { data, isLoading, error, isFetching } = useMonthlyReport(yearMonth)

  function setMonth(date: Date) {
    setParams({ ym: format(date, 'yyyy-MM') })
  }

  async function handleDownloadPdf() {
    setDownloadingPdf(true)
    try {
      const filename = await downloadMonthlyReportPdf(yearMonth)
      toast.success(`PDF pobrany: ${filename}`)
    } catch (err) {
      toast.error(apiErrorMessage(err))
    } finally {
      setDownloadingPdf(false)
    }
  }

  async function handleDownloadXlsx() {
    setDownloadingXlsx(true)
    try {
      const filename = await downloadMonthlyXlsx(yearMonth)
      toast.success(`Excel pobrany: ${filename}`)
    } catch (err) {
      toast.error(apiErrorMessage(err))
    } finally {
      setDownloadingXlsx(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="secondary"
            onClick={() => setMonth(subMonths(yearMonthToDate(yearMonth), 1))}
            aria-label="Poprzedni miesiąc"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Button>
          <h1 className="text-xl sm:text-2xl font-semibold capitalize px-2">
            {formatPolishMonth(yearMonth)}
          </h1>
          <Button
            variant="secondary"
            onClick={() => setMonth(addMonths(yearMonthToDate(yearMonth), 1))}
            aria-label="Następny miesiąc"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={handleDownloadPdf}
            loading={downloadingPdf}
            disabled={downloadingPdf || isLoading}
          >
            <FileText className="h-4 w-4" aria-hidden />
            Eksport PDF
          </Button>
          <Button
            variant="secondary"
            onClick={handleDownloadXlsx}
            loading={downloadingXlsx}
            disabled={downloadingXlsx || isLoading}
          >
            <Download className="h-4 w-4" aria-hidden />
            Eksport XLSX
          </Button>
        </div>
      </div>

      {error && <Alert variant="error">{apiErrorMessage(error)}</Alert>}

      {(isLoading || isFetching) && (
        <p className="text-sm text-text-muted inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          {isLoading ? 'Wczytuję raport…' : 'Odświeżam…'}
        </p>
      )}

      {data && !isLoading && (
        <>
          <div className="rounded-card border border-border-subtle bg-bg-elevated px-4 py-3 text-sm flex flex-wrap gap-x-6 gap-y-1">
            <span>
              <span className="text-text-muted">Pracownicy:</span>{' '}
              <span className="font-medium">
                {data.team.employeesReported}
              </span>
              <span className="text-text-faint">
                {' '}
                (z aktywnością: {data.team.employeesWithActivity})
              </span>
            </span>
            <span>
              <span className="text-text-muted">Czas pracy:</span>{' '}
              <span className="font-medium font-mono">
                {data.team.totalHours.toFixed(2)} h
              </span>
            </span>
            <span>
              <span className="text-text-muted">Nieobecności:</span>{' '}
              <span
                className="font-medium font-mono"
                title="L4 zalicza 80% dnia, urlop bezpłatny 0%"
              >
                {data.team.totalVacationHours.toFixed(2)} h
              </span>
            </span>
            <span>
              <span className="text-text-muted">Razem do rozliczenia:</span>{' '}
              <span className="font-medium font-mono">
                {data.team.totalCreditedHours.toFixed(2)} h
              </span>
            </span>
            <span>
              <span className="text-text-muted">Sesji:</span>{' '}
              <span className="font-medium font-mono">
                {data.team.totalSessions}
              </span>
            </span>
            <span>
              <span className="text-text-muted">Dni z aktywnością:</span>{' '}
              <span className="font-medium font-mono">
                {data.team.daysWithActivity}
              </span>
            </span>
            <span>
              <span className="text-text-muted">Szacunek total (netto):</span>{' '}
              <span className="font-medium font-mono">
                {data.team.totalPayroll.toLocaleString('pl-PL', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                zł
              </span>
            </span>
          </div>

          {data.users.length === 0 && (
            <div className="rounded-card border border-border-subtle bg-bg-elevated p-8 text-center text-text-muted">
              Brak pracowników do pokazania w tym miesiącu.
            </div>
          )}

          {data.users.length > 0 && (
            <>
            {/* Admin i manager pracują głównie z telefonu — 14 kolumn nie zmieści
                się na ekranie, więc tabela scrolluje w poziomie, a kolumna
                z nazwiskiem zostaje przypięta, żeby wiadomo było, czyj to wiersz. */}
            <p className="sm:hidden text-[11px] text-text-faint flex items-center gap-1.5 mb-2">
              <MoveHorizontal className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Przesuń tabelę w bok, żeby zobaczyć pozostałe kolumny. Nazwisko
              zostaje na miejscu.
            </p>
            <div className="rounded-card border border-border-subtle bg-bg-elevated overflow-hidden">
              <div className="overflow-x-auto overscroll-x-contain">
                <table className="w-full min-w-[1080px] text-sm">
                  <thead className="bg-bg-surface text-text-muted text-xs uppercase tracking-wider">
                    <tr>
                      <th
                        rowSpan={2}
                        className={cn(
                          'text-left px-4 py-2 font-medium align-bottom',
                          STICKY_COL,
                          'bg-bg-surface z-20',
                        )}
                      >
                        Pracownik
                      </th>
                      <th
                        rowSpan={2}
                        className="text-left px-4 py-2 font-medium align-bottom"
                      >
                        Stanowisko
                      </th>
                      <th
                        rowSpan={2}
                        className="text-right px-3 py-2 font-medium align-bottom"
                      >
                        Praca (h)
                      </th>
                      <th
                        colSpan={VACATION_COLUMNS.length}
                        className="text-center px-3 py-2 font-medium border-l border-border-subtle text-[10px]"
                      >
                        Nieobecności (dni)
                      </th>
                      <th
                        rowSpan={2}
                        className="text-right px-3 py-2 font-medium align-bottom border-l border-border-subtle"
                      >
                        Nieob. (h)
                      </th>
                      <th
                        rowSpan={2}
                        className="text-right px-3 py-2 font-medium align-bottom"
                      >
                        Razem (h)
                      </th>
                      <th
                        rowSpan={2}
                        className="text-right px-3 py-2 font-medium align-bottom"
                      >
                        Dni
                      </th>
                      <th
                        rowSpan={2}
                        className="text-right px-3 py-2 font-medium align-bottom"
                      >
                        Poza
                      </th>
                      <th
                        rowSpan={2}
                        className="text-right px-3 py-2 font-medium align-bottom"
                      >
                        Saldo urlopu
                      </th>
                      <th
                        rowSpan={2}
                        className="text-right px-4 py-2 font-medium align-bottom"
                      >
                        Szacunek (netto)
                      </th>
                    </tr>
                    <tr>
                      {VACATION_COLUMNS.map((col, i) => (
                        <th
                          key={col.type}
                          title={col.title}
                          className={cn(
                            'text-right px-2 pb-2 font-medium text-[10px] normal-case',
                            i === 0 && 'border-l border-border-subtle',
                            col.type === 'CHOROBOWY' && 'text-warning',
                          )}
                        >
                          {col.short}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.map((u) => {
                      const idle = !u.summary.hasActivity
                      return (
                        <tr
                          key={u.user.id}
                          className={cn(
                            'border-t border-border-subtle hover:bg-bg-surface/50 transition',
                            idle && 'text-text-faint',
                          )}
                        >
                          <td
                            className={cn(
                              'px-4 py-3',
                              STICKY_COL,
                              'bg-bg-elevated z-10',
                            )}
                          >
                            <Link
                              to={`/admin/report/${u.user.id}?ym=${yearMonth}`}
                              className={cn(
                                'font-medium hover:text-brand-accent',
                                idle ? 'text-text-muted' : 'text-text-primary',
                              )}
                            >
                              {u.user.name}
                            </Link>
                            <div className="text-xs text-text-muted">
                              {idle ? (
                                <span className="text-text-faint">
                                  brak aktywności
                                </span>
                              ) : (
                                <span className="hidden sm:inline">
                                  {u.user.email}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-text-muted">
                            {u.user.position?.name ?? (
                              <span className="text-text-faint">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right font-mono">
                            {u.summary.totalHours.toFixed(2)}
                          </td>
                          {VACATION_COLUMNS.map((col, i) => {
                            const days = u.payroll.vacationDaysByType[col.type]
                            return (
                              <td
                                key={col.type}
                                className={cn(
                                  'px-2 py-3 text-right font-mono',
                                  i === 0 && 'border-l border-border-subtle',
                                  days > 0 &&
                                    col.type === 'CHOROBOWY' &&
                                    'text-warning',
                                )}
                              >
                                {days > 0 ? (
                                  days
                                ) : (
                                  <span className="text-text-faint">·</span>
                                )}
                              </td>
                            )
                          })}
                          <td className="px-3 py-3 text-right font-mono border-l border-border-subtle">
                            {u.summary.vacationHours > 0 ? (
                              <span
                                title={u.payroll.vacationLines
                                  .filter((l) => l.percent > 0)
                                  .map(
                                    (l) =>
                                      `${VACATION_SHORT[l.type]}: ${l.days} d × ${(
                                        u.payroll.dailyHours *
                                        (l.percent / 100)
                                      ).toFixed(2)} h`,
                                  )
                                  .join(' · ')}
                              >
                                {u.summary.vacationHours.toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-text-faint">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right font-mono font-medium">
                            {u.summary.creditedHours.toFixed(2)}
                          </td>
                          <td className="px-3 py-3 text-right font-mono">
                            {u.summary.daysWorked}
                          </td>
                          <td className="px-3 py-3 text-right">
                            {u.summary.outsideClinicCount > 0 ? (
                              <span
                                className={cn(
                                  'inline-flex items-center rounded-pill px-2 py-0.5 text-xs',
                                  'bg-warning/10 text-warning',
                                )}
                              >
                                {u.summary.outsideClinicCount}
                              </span>
                            ) : (
                              <span className="text-text-faint">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right font-mono whitespace-nowrap">
                            {u.vacationBalance ? (
                              <span
                                title={`Wykorzystane ${u.vacationBalance.usedDays} z ${u.vacationBalance.totalDays} dni w ${u.vacationBalance.year}`}
                              >
                                {u.vacationBalance.remainingDays}
                                <span className="text-text-faint">
                                  {' / '}
                                  {u.vacationBalance.totalDays}
                                </span>
                              </span>
                            ) : (
                              <span className="text-text-faint">—</span>
                            )}
                            {u.sickLeaveDisplacedDays > 0 && (
                              <span
                                className="text-brand-accent ml-1 font-bold cursor-help"
                                title={`${u.sickLeaveDisplacedDays} dni urlopu wypadło na L4 (art. 166 KP) — choroba przerwała urlop, więc te dni wróciły do puli i saldo już je uwzględnia`}
                              >
                                *
                              </span>
                            )}
                          </td>
                          <td
                            className={cn(
                              'px-4 py-3 text-right font-mono tabular-nums',
                              u.payroll.hourlyRate === null && 'text-text-faint',
                            )}
                          >
                            {u.payroll.hourlyRate === null ? (
                              <span title="Nie ustawiono stawki godzinowej">
                                —
                              </span>
                            ) : (
                              <>
                                {u.payroll.total.toLocaleString('pl-PL', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{' '}
                                zł
                              </>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  {/* Podsumowanie na końcu tabeli — na telefonie to jedyne
                      miejsce, w którym widać sumy bez przewijania w górę. */}
                  <tfoot className="bg-bg-surface font-medium">
                    <tr className="border-t-2 border-border-strong">
                      <td
                        className={cn(
                          'px-4 py-3',
                          STICKY_COL,
                          'bg-bg-surface z-10',
                        )}
                      >
                        Razem
                        <div className="text-xs text-text-muted font-normal">
                          {data.team.employeesReported}{' '}
                          {pluralOsoby(data.team.employeesReported)}
                        </div>
                      </td>
                      <td className="px-4 py-3" />
                      <td className="px-3 py-3 text-right font-mono">
                        {data.team.totalHours.toFixed(2)}
                      </td>
                      {VACATION_COLUMNS.map((col, i) => {
                        const days = data.team.totalVacationDaysByType[col.type]
                        return (
                          <td
                            key={col.type}
                            className={cn(
                              'px-2 py-3 text-right font-mono',
                              i === 0 && 'border-l border-border-subtle',
                              days > 0 &&
                                col.type === 'CHOROBOWY' &&
                                'text-warning',
                            )}
                          >
                            {days > 0 ? (
                              days
                            ) : (
                              <span className="text-text-faint">·</span>
                            )}
                          </td>
                        )
                      })}
                      <td className="px-3 py-3 text-right font-mono border-l border-border-subtle">
                        {data.team.totalVacationHours.toFixed(2)}
                      </td>
                      <td className="px-3 py-3 text-right font-mono">
                        {data.team.totalCreditedHours.toFixed(2)}
                      </td>
                      <td className="px-3 py-3 text-right font-mono">
                        {data.users.reduce(
                          (sum, u) => sum + u.summary.daysWorked,
                          0,
                        )}
                      </td>
                      <td className="px-3 py-3 text-right font-mono">
                        {data.users.reduce(
                          (sum, u) => sum + u.summary.outsideClinicCount,
                          0,
                        ) || <span className="text-text-faint">—</span>}
                      </td>
                      <td className="px-3 py-3 text-right text-text-faint">—</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">
                        {data.team.totalPayroll.toLocaleString('pl-PL', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        zł
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            {data.users.some((u) => u.sickLeaveDisplacedDays > 0) && (
              <p className="text-[11px] text-text-faint text-center mt-4 leading-relaxed">
                <span className="text-brand-accent font-medium">*</span> Choroba
                przerywa urlop (art. 166 KP) — te dni liczą się jako L4, nie jako
                wypoczynkowy, i wróciły do puli urlopowej. Saldo obok już to
                uwzględnia.
              </p>
            )}
            <p className="text-[11px] text-text-faint text-center mt-6 leading-relaxed">
              Chorobowe (L4) zalicza 80% dnia pracy i jest płatne pełną stawką od
              tych godzin. Urlop bezpłatny nie wchodzi ani do godzin, ani do
              wynagrodzenia. Szacunek orientacyjny — realne wynagrodzenie wg umowy
              i przepisów nalicza księgowa. Po 33 dniach L4 płatność przejmuje ZUS,
              więc koszty firmy mogą być niższe niż szacunek.
            </p>
            </>
          )}
        </>
      )}
    </div>
  )
}
